import ColorButtonGroup from '@components/v2/ui/ColorButton/ColorButtonGroup';
import { Box, Stack } from '@mui/material';
import { MouseEvent } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useShallow } from 'zustand/react/shallow';
import useWidth from '../../../../components/Hooks/useWidth';
import Typography from '../../../../components/v2/ui/Typography/Typography';
import storage from '../../../../hooks/useStorage/useStorage';
import { ProductDetailsColor } from '../../../../services/modules/productDetails/productDetails.types';
import useProductDetailsStore from '../../../../stores/ProductDetails/productDetails.store';
import { prevent } from '../../../../utils/generics';
import { useProductDetailsStyles } from '../styles/product-details.styles';

interface ProductColorFilterProps {
  colors: ProductDetailsColor[];
  productColorCode: string;
  productColorName: string;
}

const useStyles = makeStyles()(() => ({
  hideScroll: {
    '&::-webkit-scrollbar-thumb': {
      display: 'none',
    },
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '& > div': {
      display: 'flex',
    },
  },
}));

const toColorButtonItem = (color: ProductDetailsColor) => ({
  id: color.productColorId,
  label: color.name.toLocaleLowerCase(),
  hexcode: color.hexcode,
  imageUrl: color.isImage ? color.imageUrl : undefined,
});

const ProductColorFilter = ({
  colors,
  productColorCode,
  productColorName,
}: ProductColorFilterProps) => {
  const allColors = colors || [];
  const { mediumDownWidth } = useWidth();
  const { classes, cx } = useStyles();
  const { classes: productDetailsClasses } = useProductDetailsStyles();

  const {
    setProductDetailsModalState,
    productDetailsModalState,
    removeCopiedGrid,
    handleChangeProductGridParams,
    productGridParams,
  } = useProductDetailsStore(
    useShallow((state) => ({
      productGridParams: state.productGridParams,
      productDetailsModalState: state.productDetailsModalState,
      removeCopiedGrid: state.removeCopiedGrid,
      setProductDetailsModalState: state.setProductDetailsModalState,
      handleChangeProductGridParams: state.handleChangeProductGridParams,
    })),
  );

  const selectedColorId =
    allColors.length === 1 ? allColors[0].productColorId : productGridParams.productColorId;

  const handleChangeColor = (productColorId: string, event: MouseEvent<HTMLButtonElement>) => {
    prevent(event);
    removeCopiedGrid();
    setProductDetailsModalState({
      isOpen: true,
      productColorId,
      productId: productDetailsModalState.productId,
      disableGrid: productDetailsModalState?.disableGrid,
      disablePrice: productDetailsModalState?.disablePrice,
    });
    handleChangeProductGridParams({
      ...productGridParams,
      productColorId,
    });

    storage.setProductDetailsFilter(storage.getSessionId() as string, {
      isAllColorsFilterSelected: false,
    });
  };

  const colorButtons = (
    <ColorButtonGroup
      items={allColors.map(toColorButtonItem)}
      selectedId={selectedColorId}
      size="lg"
      wrap={!mediumDownWidth}
      tabIndex={-1}
      onSelect={handleChangeColor}
    />
  );

  const colorLabel = (
    <Stack direction="row" spacing={1 / 2} alignItems="baseline">
      <Typography variant="micro2" color="text.secondary">
        {productColorCode}
      </Typography>
      <Typography variant="body2" color="text.primary">
        {productColorName.toLocaleLowerCase()}
      </Typography>
    </Stack>
  );

  if (mediumDownWidth) {
    return (
      <Stack spacing={1 / 2} sx={{ width: '100%' }}>
        {colorLabel}
        <Box className={cx(productDetailsClasses.mobileNoWrapRoot, classes.hideScroll)}>
          {colorButtons}
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={1 / 2}>
      {colorLabel}
      {colorButtons}
    </Stack>
  );
};

export default ProductColorFilter;
